import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchInput } from "@/components/pawkt/SearchInput";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Package, Minus, Trash2, AlertTriangle, Upload, PackageMinus, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";
import { InternalConsumptionModal, ConsumptionLogModal } from "@/components/pawkt/InternalConsumption";

const CATS = ["food", "medicine", "grooming", "accessories", "toys", "supplements", "treats", "hygiene", "equipment", "other"].map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));

export default function Inventory() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = hasPerm(user, "inventory.delete");
  const canCreate = hasPerm(user, "inventory.create");
  const canEditTargets = hasPerm(user, "inventory.edit");
  const canConsume = hasPerm(user, "inventory.consume");
  const [open, setOpen] = useState(false);
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [del, setDel] = useState(null); // product pending deletion
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");

  const { data, isLoading } = useQuery({ queryKey: ["products", search], queryFn: async () => (await api.get(`/products${search ? `?search=${encodeURIComponent(search)}` : ""}`)).data });
  const add = useMutation({ mutationFn: (b) => api.post("/products", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); setOpen(false); toast.success("Product added"); }, onError: (e) => toast.error(apiErr(e)) });
  const adjust = useMutation({ mutationFn: ({ id, delta }) => api.post(`/products/${id}/adjust`, { delta, reason: "adjustment" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }) });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      setDel(null);
      toast.success(res.data?.deactivated ? "Product archived (has sales/stock history)" : "Product deleted");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const stockBadge = (p) => {
    if (p.stock <= 0) return <span className="rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs">Out</span>;
    if (p.stock <= p.min_stock) return <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs">Low</span>;
    return <span className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs">In stock</span>;
  };
  const totalValue = (data || []).reduce((s, p) => s + (p.stock || 0) * (p.purchase_price || 0), 0);

  return (
    <div>
      <PageHeader title="Inventory" subtitle={`Stock value ${inr(totalValue)}`}>
        {canConsume && <Button variant="outline" onClick={() => setLogOpen(true)} data-testid="consumption-log-btn" className="gap-1.5"><History className="h-4 w-4" /> Consumption Log</Button>}
        {canConsume && <Button variant="outline" onClick={() => setConsumeOpen(true)} data-testid="internal-consumption-btn" className="gap-1.5"><PackageMinus className="h-4 w-4" /> Internal Use</Button>}
        {canCreate && <Button variant="outline" onClick={() => navigate("/inventory-import")} data-testid="bulk-upload-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Bulk Upload</Button>}
        <Button onClick={() => setOpen(true)} data-testid="add-product-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"><Plus className="h-4 w-4" /> Add Product</Button>
      </PageHeader>

      <div className="mb-4 max-w-md">
        <SearchInput value={search} onChange={setSearch} testid="inventory-search" placeholder="Search products by name, SKU or category…" />
      </div>

      {isLoading ? <Loading /> : (data?.length ? (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Product</th><th className="px-4 py-3 hidden sm:table-cell">SKU</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-center">Stock</th><th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-center"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`product-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 hidden sm:table-cell">{p.sku}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{p.category}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular">{inr(p.selling_price)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => adjust.mutate({ id: p.id, delta: -1 })} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Minus className="h-3 w-3" /></button>
                      <span className="w-8 text-center tabular font-medium">{p.stock}</span>
                      <button onClick={() => adjust.mutate({ id: p.id, delta: 1 })} className="h-6 w-6 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100"><Plus className="h-3 w-3" /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">{stockBadge(p)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {canDelete && <button onClick={() => setDel(p)} data-testid={`product-delete-${p.id}`} aria-label={`Delete ${p.name}`}
                        className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="h-4 w-4" /></button>}
                      <ApprovalRequestButton action="inventory.delete" targetId={p.id} targetLabel={p.name} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty icon={Package} title="No products yet" />)}

      <FormModal open={open} onOpenChange={setOpen} title="Add Product" testid="product-modal"
        initial={{ category: "food", gst: 18, min_stock: 5, stock: 0, selling_price: 0 }} submitLabel="Add product"
        description="A SKU is generated automatically by Pawkt (e.g. PAW-FD-000001)."
        fields={[
          { name: "name", label: "Product name", full: true },
          { name: "category", label: "Category", type: "select", options: CATS },
          { name: "brand", label: "Brand" },
          { name: "weight", label: "Weight" },
          { name: "unit", label: "Unit (e.g. KG)" },
          { name: "purchase_price", label: "Cost price (₹)", type: "number" },
          { name: "selling_price", label: "Sale price (₹)", type: "number" },
          { name: "stock", label: "Opening stock", type: "number" },
          { name: "min_stock", label: "Min stock", type: "number" },
          ...(canEditTargets ? [
            { name: "target_stock", label: "Stock target (Order Soon, optional)", type: "number" },
            { name: "lead_time_days", label: "Lead time days (optional)", type: "number" },
          ] : []),
        ]}
        onSubmit={(v) => add.mutateAsync({ ...v, target_stock: Number(v.target_stock) > 0 ? Number(v.target_stock) : null, lead_time_days: Number(v.lead_time_days) > 0 ? Number(v.lead_time_days) : null })} />

      <Dialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }}>
        <DialogContent className="bg-white max-w-md" data-testid="product-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" /> Delete inventory item?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will remove <span className="font-semibold text-slate-900">{del?.name}</span> from the inventory list. This action cannot be undone.
          </p>
          <p className="text-xs text-slate-400">If this item has sales or stock history, it will be archived (hidden from active inventory and POS) so past records stay intact.</p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDel(null)} data-testid="product-delete-cancel" className="min-h-[44px]">Cancel</Button>
            <Button onClick={() => del && remove.mutate(del.id)} disabled={remove.isPending} data-testid="product-delete-confirm" className="min-h-[44px] bg-rose-600 hover:bg-rose-700 text-white">
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InternalConsumptionModal open={consumeOpen} onOpenChange={setConsumeOpen} products={data || []} onDone={() => qc.invalidateQueries({ queryKey: ["products"] })} />
      <ConsumptionLogModal open={logOpen} onOpenChange={setLogOpen} canConsume={canConsume} onReversed={() => qc.invalidateQueries({ queryKey: ["products"] })} />
    </div>
  );
}
